import React from 'react';

/** Configuration for a single step in the tab navigator. */
export interface StepTabConfig {
  /** Display label shown next to the step number. */
  label: string;
}

export interface StepTabNavProps {
  /** Ordered list of step definitions. */
  steps: StepTabConfig[];
  /** Currently active step (1-based). */
  currentStep: number;
  /**
   * Highest step the user has unlocked. Tabs at or below this index are
   * clickable (except the active tab itself). Tabs above it are locked.
   */
  maxStep: number;
  /**
   * Called when a completed (non-active) tab is clicked.
   * Receives the 1-based step number.
   */
  onStepClick?: (step: number) => void;
}

/**
 * Horizontal tab-style step navigator used in multi-step wizard flows.
 *
 * - Active tab: highlighted in `secondary-color-40` with a coloured bottom border.
 * - Completed tabs: highlighted in `success-50` with a checkmark icon; clickable.
 * - Locked tabs: muted, not interactive.
 *
 * The component is display-only — step state is owned by the parent.
 *
 * @example
 * ```tsx
 * const STEPS = [{ label: 'Szczegóły' }, { label: 'Dane' }, { label: 'Podgląd' }];
 * <StepTabNav steps={STEPS} currentStep={step} maxStep={maxStep} onStepClick={setStep} />
 * ```
 */
const StepTabNav: React.FC<StepTabNavProps> = ({
  steps,
  currentStep,
  maxStep,
  onStepClick,
}) => (
  <nav className="flex flex-row w-full gap-0" aria-label="Kroki formularza">
    {steps.map(({ label }, index) => {
      const num = index + 1;
      const isActive = num === currentStep;
      const isCompleted = num < maxStep;
      const isUnlocked = num <= maxStep;
      const isClickable = isUnlocked && !isActive;
      const isLast = index === steps.length - 1;

      const textClass = isActive
        ? 'text-secondary-color-40'
        : isCompleted
          ? 'text-success-50'
          : 'text-neutral-60';

      const borderBottomClass = isActive
        ? 'border-secondary-color-40'
        : isCompleted
          ? 'border-success-50'
          : 'border-[#DCDDE0]';

      const labelColor = isActive || isCompleted ? '#181A1B' : undefined;

      return (
        <div
          key={num}
          role={isClickable ? 'button' : undefined}
          tabIndex={isClickable ? 0 : undefined}
          aria-current={isActive ? 'step' : undefined}
          aria-disabled={!isUnlocked}
          onClick={
            isClickable && onStepClick ? () => onStepClick(num) : undefined
          }
          onKeyDown={
            isClickable && onStepClick
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    onStepClick(num);
                  }
                }
              : undefined
          }
          className={[
            'flex flex-col flex-1 border-b-2',
            borderBottomClass,
            !isLast ? 'border-r border-r-[#DCDDE0]' : '',
            isClickable ? 'cursor-pointer select-none' : '',
            !isUnlocked ? 'cursor-not-allowed' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <div className={`w-full font-medium py-2 ${textClass}`}>
            <span className="flex items-center justify-start gap-4">
              <span className="font-semibold text-[24px] ml-3">
                {String(num).padStart(2, '0')}
              </span>
              <span
                className="text-[14px] flex-1 text-left whitespace-nowrap"
                style={labelColor ? { color: labelColor } : undefined}
              >
                {label}
              </span>
              {isCompleted && !isActive && (
                <div className="mr-4">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M5 13L9 17L19 7"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}
            </span>
          </div>
        </div>
      );
    })}
  </nav>
);

export default StepTabNav;
